import { firstError, resolveLabels, unwrap, Widget, WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { CampaignList } from "./CampaignsView";
import { CAMPAIGN_LIST_LABELS } from "./labels";
import { buildTenantGroups } from "@nexus/widget-kit";
import type { CampaignListConfig, CampaignScheduleData } from "./types";

export const LABELS = CAMPAIGN_LIST_LABELS;

export const manifest: WidgetManifest = {
  id: "campaign-list",
  name: "Campaign List",
  description: "Campaigns and their protected events for one tenant.",
  version: "1.0.0",
  dataRequests: {
    schedule: {
      source: "campaign-schedule",
      contract: { name: "campaign-schedule", version: 1 },
      configurable: false,
    },
  },
  labels: LABELS,
};

export const defaultConfig: CampaignListConfig = {
  title: "Campaigns",
  status: "active",
};

export function Component({ data, config, dashboard }: WidgetProps<CampaignListConfig>) {
  const labels = resolveLabels(LABELS, config);
  const error = firstError(data, ["schedule"]);
  const resolved = unwrap<CampaignScheduleData>(data.schedule);
  const rows = resolved?.rows ?? [];
  const nowMs = config.nowMs ?? Date.now();
  const status = config.status ?? "active";
  const tenantGroups = buildTenantGroups(rows, nowMs);
  const campaigns = tenantGroups
    .filter((tenant) => !config.tenantId || tenant.tenant_id === config.tenantId)
    .flatMap((tenant) => tenant.campaigns)
    .filter((campaign) => campaign.status === status);
  const widgetClassName = config.hideHeader ? "min-h-0 rounded-none border-0" : "";

  if (error) {
    return (
      <Widget
        title={config.title ?? defaultConfig.title}
        hideHeader={config.hideHeader}
        className={widgetClassName}
      >
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  if (rows.length === 0 && data.schedule?.isLoading) {
    return (
      <Widget
        title={config.title ?? defaultConfig.title}
        hideHeader={config.hideHeader}
        className={widgetClassName}
      >
        <WidgetSkeleton shape="matrix" />
      </Widget>
    );
  }

  if (campaigns.length === 0) {
    return (
      <Widget
        title={config.title ?? defaultConfig.title}
        hideHeader={config.hideHeader}
        className={widgetClassName}
      >
        <WidgetState
          title={`No ${status} campaigns`}
          description={labels.emptyDescription}
        />
      </Widget>
    );
  }

  return (
    <Widget
      title={config.title ?? defaultConfig.title}
      hideHeader={config.hideHeader}
      className={widgetClassName}
    >
      <CampaignList
        campaigns={campaigns}
        labels={labels}
        nowMs={nowMs}
        expandedCampaignIds={config.expandedCampaignIds ?? []}
        onCampaignOpenChange={(campaignId, open) =>
          dashboard.emitInteraction?.({
            type: "campaign.disclosure",
            payload: { campaign_id: campaignId, open },
            source: { dataKey: "schedule" },
          })
        }
        onSelectEvent={(eventId, protectedEventId) =>
          dashboard.emitInteraction?.({
            type: "campaign.event.protection",
            payload: { event_id: eventId, protected_event_id: protectedEventId },
            source: { dataKey: "schedule" },
          })
        }
        onOpenEventMonitor={
          dashboard.emitInteraction
            ? (eventId, protectedEventId) =>
                dashboard.emitInteraction?.({
                  type: "campaign.event.monitor",
                  payload: { event_id: eventId, protected_event_id: protectedEventId },
                  source: { dataKey: "schedule" },
                })
            : undefined
        }
      />
    </Widget>
  );
}

const widgetModule: WidgetModule<CampaignListConfig> = { manifest, Component, defaultConfig };

export default widgetModule;
